import { AVAILABLE_ANIMATIONS } from '@/constants/animations';
import type { SubtitleEditorData } from '@/components/SubtitleEditor';
import {
  DEFAULT_TEXT_STYLE,
  makeAudioLayer,
  makeImageLayer,
  makeTextLayer,
  makeVideoLayer,
} from '@/lib/layers/layerTypeConfig';
import { assertSoundEffectConcurrency, createSoundEffectLayer } from '@/lib/layers/soundEffectLayer';
import { applySpeedChange } from '@/lib/layers/applySpeedChange';
import type { LayerRect, MediaLayer, TextLayer } from '@/lib/layers/types';
import {
  EFFECT_SECTION_KNOBS,
  MAX_EFFECT_SECTIONS,
  MIN_SECTION_SECONDS,
  normalizeEffectSections,
  type EffectId,
} from '@/lib/video-effects/sections';
import {
  EFFECT_CONTROLS,
  LUT_PRESET_IDS,
  normalizeVideoEffects,
  type LutPresetId,
  type VideoEffects,
} from '@/lib/video-effects/types';
import {
  TRANSITION_KINDS,
  sanitizeTransitions,
  type KeyframeTransition,
  type SceneTransition,
  type TransitionKind,
} from '@/lib/transitions/types';
import { SUBTITLE_TEXT_TRANSFORMS, type SubtitleTextTransform } from '@/lib/subtitles/subtitleTextTransform';
import { reconcileTransitions } from '@/lib/transitions/reconcileTransitions';
import { splitSceneCropKeyframes } from '@/lib/timeline/naturalScenes';
import { applySceneCropFraming, resolveSceneCropTarget } from '@/lib/crop/sceneCropFraming';
import type { CropCenterKeyframe } from '@/hooks/use-auto-save-crop-keyframes';
import type { ClipCoverImage } from '@/lib/cover/coverImage';
import type { ClientCall, ClientResult } from '../clientTools';
import { TEXT_POSITIONS } from './editorTools';
import { applyMotionTool, MotionToolError } from './motionTools';
import type { TranscriptWord } from './buildEditorPageState';
import {
  LayoutCallError,
  validateLayoutCall,
  type LayoutCall,
  type LayoutCatalogEntry,
} from './layoutCatalog';
import type { AssistantMediaItem } from './mediaTypes';
import { MEDIA_PLACEMENTS, placeMediaRect, type MediaPlacement } from './mediaTools';
import {
  clipDurationOf,
  cutClipRanges,
  extendClip,
  removeScenes,
  reorderScenes,
  SceneEditError,
  splitSceneAt,
  trimClip,
  type Scene,
} from './sceneEdits';
import { isInsideRange, planCameraMove, type CameraFraming } from './cameraMove';
import {
  isSfxLayer,
  SFX_ALIGNS,
  SfxPlacementError,
  type SfxAlign,
} from './soundEffects';

// Applies one batch of editor calls to a draft of the editor state. Pure: the
// editor bridge commits each changed area once through its normal path, so a
// batch is one change the user sees (and undoes) at once.

export interface EditorDraft {
  subtitle: SubtitleEditorData;
  subtitlesEnabled: boolean;
  fontId: string;
  layers: MediaLayer[];
  videoEffects: VideoEffects;
  transitions: SceneTransition[];
  /** The natural timeline: source ranges in playback order. */
  scenes: Scene[];
  /** The framing the preview applies (user-edited or auto-derived). */
  cropKeyframes: CropCenterKeyframe[];
  /** Transitions at framing boundaries inside scenes (camera moves). */
  keyframeTransitions: KeyframeTransition[];
  cover: ClipCoverImage | null;
}

export interface EditorFont {
  id: string;
  name: string;
  fileUrl: string | null;
}

export interface EditorTemplate {
  id: string;
  name: string;
  canApply: boolean;
  /** The template's subtitle look as the editor loads it. */
  subtitle: SubtitleEditorData;
  fontId: string;
}

export interface EditorEnv {
  /** Source transcript; motion captions resolve against the live draft scenes. */
  words?: readonly TranscriptWord[];
  /** Length of the source video (the limit for editor_extend). */
  sourceDuration: number;
  /** The preview crops the source (portrait from a wider source). */
  framingAvailable: boolean;
  nextSceneId: () => number;
  layoutCatalog: readonly LayoutCatalogEntry[];
  /** A campaign enforces this clip's layout. */
  layoutEnforced: boolean;
  /** Items found by editor_search_media in this page session, by id. */
  media: ReadonlyMap<string, AssistantMediaItem>;
  /** Composition width / height (rects are fractions of it). */
  compAspect: number;
  clipCount: number;
  fonts: readonly EditorFont[];
  templates: readonly EditorTemplate[];
  newId: () => string;
}

export type EditorArea = 'subtitle' | 'layers' | 'effects' | 'transitions' | 'timeline' | 'framing';

export interface EditorBatchOutcome {
  draft: EditorDraft;
  results: ClientResult[];
  changed: Set<EditorArea>;
  templateId: string | null;
  seek: { time: number; play: boolean } | null;
  selectClipIndex: number | null;
  /** editor_set_cover: the bridge captures this clip time and sets it. */
  cover: { time: number; callId: string } | null;
  /** editor_set_layout: applied by the bridge through the layout picker path. */
  layout: LayoutCall | null;
}

class CallError extends Error {
  constructor(readonly code: string, readonly detail?: string) {
    super(code);
  }
}

type Args = Record<string, unknown>;

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const SUBTITLE_POSITION: Record<string, { position: string; marginVertical: number }> = {
  top: { position: 'top-center', marginVertical: 15 },
  middle: { position: 'middle-center', marginVertical: 50 },
  bottom: { position: 'bottom-center', marginVertical: 25 },
};
const TEXT_RECT_Y: Record<string, number> = {
  top: 0.08, upper: 0.24, center: 0.42, lower: 0.6, bottom: 0.76,
};
const TEXT_RECT = { x: 0.06, w: 0.88, h: 0.16 };
const MAX_TEXT_LENGTH = 120;
const MAX_TEXT_LAYERS = 30;
const DEFAULT_TRANSITION_SECONDS = 0.3;
const ANIMATIONS = new Set(AVAILABLE_ANIMATIONS.map((animation) => animation.name));

const has = (args: Args, key: string) => args[key] !== undefined && args[key] !== null;

function color(args: Args, key: string): string | undefined {
  if (!has(args, key)) return undefined;
  const value = args[key];
  if (typeof value !== 'string' || !HEX_COLOR.test(value.trim())) throw new CallError('invalid_color', key);
  return value.trim().toUpperCase();
}

function number(args: Args, key: string, min: number, max: number): number | undefined {
  if (!has(args, key)) return undefined;
  const value = Number(args[key]);
  if (!Number.isFinite(value)) throw new CallError('invalid_number', key);
  return Math.min(max, Math.max(min, value));
}

function bool(args: Args, key: string): boolean | undefined {
  if (!has(args, key)) return undefined;
  if (typeof args[key] !== 'boolean') throw new CallError('invalid_boolean', key);
  return args[key] as boolean;
}

function oneOf<T extends string>(args: Args, key: string, options: readonly T[]): T | undefined {
  if (!has(args, key)) return undefined;
  const value = args[key];
  if (typeof value !== 'string' || !(options as readonly string[]).includes(value)) {
    throw new CallError('invalid_option', key);
  }
  return value as T;
}

function text(args: Args, key: string): string | undefined {
  if (!has(args, key)) return undefined;
  const value = args[key];
  if (typeof value !== 'string') throw new CallError('invalid_text', key);
  const clean = value.replace(/[\p{Cc}]+/gu, ' ').trim();
  if (!clean) throw new CallError('empty_text', key);
  return clean.slice(0, MAX_TEXT_LENGTH);
}

const normalizeName = (value: string) => value.normalize('NFD').replace(/\p{Diacritic}/gu, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

function findFont(env: EditorEnv, name: string): EditorFont {
  const wanted = normalizeName(name);
  const font = env.fonts.find((candidate) => candidate.id === name)
    ?? env.fonts.find((candidate) => normalizeName(candidate.name) === wanted);
  if (!font) throw new CallError('unknown_font', 'Use a name from options.fonts');
  return font;
}

/** start/end on the clip timeline, clamped to the clip; at least one frame. */
function window(args: Args, clipDuration: number, fallback?: { start: number; end: number }) {
  const start = has(args, 'start') ? Number(args.start) : fallback?.start;
  const end = has(args, 'end') ? Number(args.end) : fallback?.end;
  if (start === undefined || end === undefined || !Number.isFinite(start) || !Number.isFinite(end)) {
    throw new CallError('invalid_time');
  }
  const duration = Math.max(0, clipDuration);
  // A start past the clip (e.g. after a cut in the same batch) is an error,
  // not a sliver squeezed into the last frame.
  if (start >= duration) throw new CallError('outside_clip', `The clip is ${Math.round(duration * 10) / 10} s long`);
  const clampedStart = Math.min(Math.max(0, start), Math.max(0, duration - MIN_SECTION_SECONDS));
  const clampedEnd = Math.min(Math.max(end, clampedStart + MIN_SECTION_SECONDS), duration);
  if (clampedEnd - clampedStart < MIN_SECTION_SECONDS - 1e-9) throw new CallError('outside_clip');
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return { start: round(clampedStart), end: round(clampedEnd) };
}

function setSubtitleStyle(draft: EditorDraft, args: Args, env: EditorEnv) {
  const next: SubtitleEditorData = { ...draft.subtitle };
  const applied: string[] = [];
  const set = <K extends keyof SubtitleEditorData>(key: K, value: SubtitleEditorData[K] | undefined, label: string) => {
    if (value === undefined) return;
    next[key] = value;
    if (!applied.includes(label)) applied.push(label);
  };
  let fontId = draft.fontId;
  if (has(args, 'font')) {
    const font = findFont(env, String(args.font));
    fontId = font.id;
    next.fontFamily = font.id;
    applied.push('font');
  }
  set('fontSize', number(args, 'size', 16, 140), 'size');
  set('textColor', color(args, 'text_color'), 'text_color');
  set('highlightEnabled', bool(args, 'highlight_enabled'), 'highlight_enabled');
  set('highlightColor', color(args, 'highlight_color'), 'highlight_color');
  set('highlightBoxEnabled', bool(args, 'highlight_box'), 'highlight_box');
  set('highlightBoxBackgroundColor', color(args, 'highlight_box_color'), 'highlight_box_color');
  if (has(args, 'animation')) {
    const animation = String(args.animation);
    if (!ANIMATIONS.has(animation)) throw new CallError('invalid_option', 'animation');
    set('animation', animation, 'animation');
  }
  const position = oneOf(args, 'position', Object.keys(SUBTITLE_POSITION));
  if (position) {
    set('position', SUBTITLE_POSITION[position].position, 'position');
    set('marginVertical', SUBTITLE_POSITION[position].marginVertical, 'position');
  }
  set('textTransform', oneOf<SubtitleTextTransform>(args, 'text_transform', SUBTITLE_TEXT_TRANSFORMS), 'text_transform');
  set('strokeEnabled', bool(args, 'outline'), 'outline');
  set('strokeColor', color(args, 'outline_color'), 'outline_color');
  set('strokeWidth', number(args, 'outline_width', 0, 30), 'outline_width');
  set('shadowEnabled', bool(args, 'shadow'), 'shadow');
  set('backgroundEnabled', bool(args, 'background'), 'background');
  set('backgroundColor', color(args, 'background_color'), 'background_color');
  set('backgroundOpacity', number(args, 'background_opacity', 0, 100), 'background_opacity');
  const words = number(args, 'max_words_per_line', 1, 12);
  set('maxWordsPerLine', words === undefined ? undefined : Math.round(words), 'max_words_per_line');
  const enabled = bool(args, 'enabled');
  if (enabled !== undefined) applied.push('enabled');
  if (!applied.length) throw new CallError('nothing_to_change');
  return {
    draft: {
      ...draft,
      subtitle: next,
      fontId,
      subtitlesEnabled: enabled ?? draft.subtitlesEnabled,
    },
    result: { applied },
  };
}

function textStyle(base: TextLayer['style'], args: Args, env: EditorEnv): TextLayer['style'] {
  const style = { ...base };
  if (has(args, 'font')) {
    const font = findFont(env, String(args.font));
    style.fontName = font.id;
    style.fontUrl = font.fileUrl;
  }
  const size = number(args, 'size', 20, 220);
  if (size !== undefined) style.fontSize = size;
  const textColor = color(args, 'color');
  if (textColor) style.color = textColor;
  if (args.background_color === 'none') {
    style.backgroundColor = null;
  } else {
    const background = color(args, 'background_color');
    if (background) {
      style.backgroundColor = background;
      style.paddingX = style.paddingX || 24;
      style.paddingY = style.paddingY || 10;
      style.borderRadius = style.borderRadius || 16;
    }
  }
  const outlineColor = color(args, 'outline_color');
  if (outlineColor) style.strokeColor = outlineColor;
  const outlineWidth = number(args, 'outline_width', 0, 30);
  if (outlineWidth !== undefined) style.strokeWidth = outlineWidth;
  if (style.strokeWidth > 0 && !style.strokeColor) style.strokeColor = '#000000';
  const bold = bool(args, 'bold');
  if (bold !== undefined) style.bold = bold;
  return style;
}

function textRect(position: string | undefined, current?: TextLayer['rect']) {
  if (!position) return current ?? { ...TEXT_RECT, y: TEXT_RECT_Y.center };
  return { ...TEXT_RECT, y: TEXT_RECT_Y[position] };
}

function addText(draft: EditorDraft, args: Args, env: EditorEnv) {
  const content = text(args, 'text');
  if (!content) throw new CallError('empty_text', 'text');
  if (draft.layers.filter((layer) => layer.type === 'text').length >= MAX_TEXT_LAYERS) {
    throw new CallError('too_many_texts');
  }
  const span = window(args, clipDurationOf(draft.scenes));
  const position = oneOf(args, 'position', TEXT_POSITIONS);
  const base = makeTextLayer({
    id: `layer-ai-${env.newId()}`, name: content, start: span.start, end: span.end,
  });
  const style = textStyle({
    ...DEFAULT_TEXT_STYLE, fontSize: 72, bold: true, strokeColor: '#000000', strokeWidth: 6,
  }, args, env);
  const rotation = number(args, 'rotation', -30, 30);
  const layer: TextLayer = {
    ...base,
    text: content,
    style,
    rect: textRect(position ?? 'center'),
    rotation: rotation ?? 0,
  };
  return {
    draft: { ...draft, layers: [...draft.layers, layer] },
    result: { id: layer.id, start: layer.start, end: layer.end },
  };
}

function updateText(draft: EditorDraft, args: Args, env: EditorEnv) {
  const id = typeof args.id === 'string' ? args.id : '';
  const current = draft.layers.find((layer) => layer.id === id);
  if (!current) throw new CallError('layer_not_found');
  if (current.type !== 'text') throw new CallError('not_a_text_layer');
  const content = text(args, 'text');
  const span = window(args, clipDurationOf(draft.scenes), { start: current.start, end: current.end });
  const position = oneOf(args, 'position', TEXT_POSITIONS);
  const rotation = number(args, 'rotation', -30, 30);
  const next: TextLayer = {
    ...current,
    ...(content ? { text: content, name: content } : {}),
    start: span.start,
    end: span.end,
    style: textStyle(current.style, args, env),
    rect: textRect(position, current.rect),
    rotation: rotation ?? current.rotation,
  };
  return {
    draft: { ...draft, layers: draft.layers.map((layer) => (layer.id === id ? next : layer)) },
    result: { id, start: next.start, end: next.end },
  };
}

function removeLayer(draft: EditorDraft, args: Args) {
  const id = typeof args.id === 'string' ? args.id : '';
  if (!draft.layers.some((layer) => layer.id === id)) throw new CallError('layer_not_found');
  return { draft: { ...draft, layers: draft.layers.filter((layer) => layer.id !== id) }, result: { removed: id } };
}

function addEffect(draft: EditorDraft, args: Args, env: EditorEnv) {
  const sections = draft.videoEffects.sections ?? [];
  if (sections.length >= MAX_EFFECT_SECTIONS) throw new CallError('too_many_effects');
  const { effect } = args;
  const span = window(args, clipDurationOf(draft.scenes));
  const intensity = number(args, 'intensity', 0, 100);
  const knob = typeof effect === 'string' ? EFFECT_SECTION_KNOBS[effect as EffectId] : undefined;
  // normalizeEffectSections fills the default intensity (80 dynamic, 100 look)
  // and rejects an unknown effect id.
  const [section] = normalizeEffectSections([{
    id: `fx-ai-${env.newId()}`,
    effect,
    start: span.start,
    end: span.end,
    ease: true,
    ...(intensity !== undefined ? { intensity } : {}),
    ...(knob ? { params: { [knob.key]: knob.default } } : {}),
  }]);
  if (!section) throw new CallError('invalid_option', 'effect');
  return {
    draft: {
      ...draft,
      videoEffects: normalizeVideoEffects({ ...draft.videoEffects, sections: [...sections, section] }),
    },
    result: { id: section.id, start: section.start, end: section.end },
  };
}

function removeEffect(draft: EditorDraft, args: Args) {
  const id = typeof args.id === 'string' ? args.id : '';
  const sections = draft.videoEffects.sections ?? [];
  if (!sections.some((section) => section.id === id)) throw new CallError('effect_not_found');
  return {
    draft: {
      ...draft,
      videoEffects: normalizeVideoEffects({
        ...draft.videoEffects, sections: sections.filter((section) => section.id !== id),
      }),
    },
    result: { removed: id },
  };
}

function setTransition(draft: EditorDraft, args: Args) {
  const cutCount = Math.max(0, draft.scenes.length - 1);
  if (cutCount === 0) throw new CallError('no_cuts', 'The clip has a single scene');
  const kind = oneOf(args, 'kind', [...TRANSITION_KINDS, 'none'] as const);
  if (!kind) throw new CallError('invalid_option', 'kind');
  const all = bool(args, 'all') === true;
  let cuts: number[];
  if (all) {
    cuts = Array.from({ length: cutCount }, (_, index) => index);
  } else {
    const after = Number(args.after_scene);
    if (!Number.isInteger(after) || after < 0 || after >= cutCount) {
      throw new CallError('invalid_scene', `after_scene must be 0-${cutCount - 1}`);
    }
    cuts = [after];
  }
  const rest = draft.transitions.filter((transition) => !cuts.includes(transition.afterSceneIndex));
  const added = kind === 'none' ? [] : cuts.map((cut) => ({
    afterSceneIndex: cut,
    kind: kind as TransitionKind,
    durationSec: number(args, 'duration', 0.1, 1.5) ?? DEFAULT_TRANSITION_SECONDS,
    ...(has(args, 'direction') ? { direction: oneOf(args, 'direction', ['left', 'right', 'up', 'down'] as const) } : {}),
  }));
  const transitions = sanitizeTransitions(
    [...rest, ...added].sort((a, b) => a.afterSceneIndex - b.afterSceneIndex),
    draft.scenes.length,
  );
  return { draft: { ...draft, transitions }, result: { cuts, kind } };
}

const LOOK_KEYS = ['exposure', 'contrast', 'saturation', 'temperature', 'vibrance', 'vignette', 'grain', 'grayscale'] as const;

function setLook(draft: EditorDraft, args: Args) {
  const next: VideoEffects = { ...draft.videoEffects, enabled: true };
  const applied: string[] = [];
  const preset = oneOf(args, 'preset', [...LUT_PRESET_IDS, 'none'] as const);
  const strength = number(args, 'strength', 0, 100);
  if (preset === 'none') {
    next.lut = null;
    applied.push('preset');
  } else if (preset) {
    next.lut = { preset: preset as LutPresetId, strength: (strength ?? 100) / 100 };
    applied.push('preset');
  } else if (strength !== undefined && next.lut) {
    next.lut = { ...next.lut, strength: strength / 100 };
    applied.push('strength');
  }
  LOOK_KEYS.forEach((key) => {
    const control = EFFECT_CONTROLS.find((entry) => entry.key === key);
    if (!control) return;
    const value = number(args, key, control.min, control.max);
    if (value === undefined) return;
    next[key] = value;
    applied.push(key);
  });
  if (!applied.length) throw new CallError('nothing_to_change');
  return { draft: { ...draft, videoEffects: normalizeVideoEffects(next) }, result: { applied } };
}

const MAX_MEDIA_LAYERS = 20;
const DEFAULT_IMAGE_SECONDS = 3;
const DEFAULT_MEDIA_SECONDS = 5;
const DEFAULT_AUDIO_VOLUME = 0.3;

function rectArg(args: Args): LayerRect | undefined {
  if (!has(args, 'rect')) return undefined;
  const raw = args.rect as Record<string, unknown>;
  const values = ['x', 'y', 'w', 'h'].map((key) => Number(raw?.[key]));
  if (!raw || typeof raw !== 'object' || values.some((value) => !Number.isFinite(value))) {
    throw new CallError('invalid_rect', 'rect needs x, y, w, h as fractions of the frame');
  }
  const [x, y, w, h] = values;
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  return { x: clamp(x, -1, 2), y: clamp(y, -1, 2), w: clamp(w, 0.02, 3), h: clamp(h, 0.02, 3) };
}

const isPlayableMedia = (layer: MediaLayer) => layer.type === 'audio' || (layer.type === 'video' && layer.source !== 'clip');

function assertSfxConcurrency(layers: readonly MediaLayer[], layer: MediaLayer) {
  try {
    assertSoundEffectConcurrency(layers, layer);
  } catch (error) {
    if (error instanceof SfxPlacementError) throw new CallError(error.code, error.detail);
    throw error;
  }
}

function addSoundEffect(draft: EditorDraft, args: Args, env: EditorEnv, item: AssistantMediaItem & { sfxId: string }) {
  const volume = number(args, 'volume', 0, 100);
  let layer: MediaLayer;
  try {
    layer = createSoundEffectLayer({
      id: `layer-ai-${env.newId()}`,
      sound: { id: item.sfxId, name: item.name, url: item.url, duration: item.duration && item.duration > 0 ? item.duration : DEFAULT_MEDIA_SECONDS, peak_at: item.peakAt ?? 0 },
      layers: draft.layers,
      at: has(args, 'start') ? Number(args.start) : 0,
      align: oneOf<SfxAlign>(args, 'align', SFX_ALIGNS) ?? 'start',
      clipDuration: clipDurationOf(draft.scenes),
      end: has(args, 'end') ? Number(args.end) : undefined,
      volume: volume === undefined ? undefined : volume / 100,
    });
  } catch (error) {
    if (error instanceof SfxPlacementError) throw new CallError(error.code, error.detail);
    throw error;
  }
  return {
    draft: { ...draft, layers: [...draft.layers, layer] },
    result: { id: layer.id, kind: 'audio', sfx: item.sfxId, start: layer.start, end: layer.end },
  };
}

function addMedia(draft: EditorDraft, args: Args, env: EditorEnv) {
  const item = env.media.get(typeof args.item_id === 'string' ? args.item_id : '');
  if (!item) throw new CallError('unknown_media', 'Use an item_id from editor_search_media');
  if (item.provider === 'sfx' && typeof item.sfxId === 'string') {
    return addSoundEffect(draft, args, env, item as AssistantMediaItem & { sfxId: string });
  }
  // Text and motion layers have their own limits, and so do sound effects.
  if (draft.layers.filter((layer) => layer.type !== 'text' && layer.type !== 'motion' && !isSfxLayer(layer)).length >= MAX_MEDIA_LAYERS) {
    throw new CallError('too_many_layers');
  }
  const duration = clipDurationOf(draft.scenes);
  const id = `layer-ai-${env.newId()}`;
  const mediaLength = item.kind === 'image' ? null : (item.duration && item.duration > 0 ? item.duration : DEFAULT_MEDIA_SECONDS);
  const start = has(args, 'start') ? Number(args.start) : 0;
  const span = window({
    start,
    end: has(args, 'end') ? args.end : start + (mediaLength ?? DEFAULT_IMAGE_SECONDS),
  }, duration);
  const end = mediaLength === null ? span.end : Math.min(span.end, Math.round((span.start + mediaLength) * 1000) / 1000);
  const opacity = number(args, 'opacity', 0, 100);
  const volume = number(args, 'volume', 0, 100);
  const placement = oneOf<MediaPlacement>(args, 'placement', MEDIA_PLACEMENTS) ?? 'fill';
  const mediaAspect = item.width && item.height ? item.width / item.height : null;
  const rect = () => placeMediaRect({ placement, rect: rectArg(args), mediaAspect, compAspect: env.compAspect });
  let layer: MediaLayer;
  if (item.kind === 'image') {
    layer = {
      ...makeImageLayer({ id, name: item.name, url: item.url, clipDuration: duration }),
      start: span.start, end, rect: rect(), uploading: false,
    };
  } else if (item.kind === 'video') {
    layer = {
      ...makeVideoLayer({ id, name: item.name, url: item.url, start: span.start, end, mediaDuration: mediaLength ?? end - span.start }),
      rect: rect(),
      uploading: false,
      // B-roll plays over the clip's own audio: muted unless asked.
      volume: volume === undefined ? 0 : volume / 100,
    };
  } else {
    layer = {
      ...makeAudioLayer({ id, name: item.name, url: item.url, start: span.start, end, mediaDuration: mediaLength ?? end - span.start }),
      uploading: false,
      volume: volume === undefined ? DEFAULT_AUDIO_VOLUME : volume / 100,
    };
  }
  if (opacity !== undefined) layer = { ...layer, opacity: opacity / 100 };
  return {
    draft: { ...draft, layers: [...draft.layers, layer] },
    result: { id, kind: item.kind, start: layer.start, end: layer.end },
  };
}

function updateLayer(draft: EditorDraft, args: Args, env: EditorEnv) {
  const id = typeof args.id === 'string' ? args.id : '';
  const current = draft.layers.find((layer) => layer.id === id);
  if (!current) throw new CallError('layer_not_found');
  const duration = clipDurationOf(draft.scenes);
  let next: MediaLayer = { ...current };
  const applied: string[] = [];
  if (has(args, 'start') || has(args, 'end')) {
    const span = window(args, duration, { start: current.start, end: current.end });
    if (isPlayableMedia(next) && (next.type === 'video' || next.type === 'audio')) {
      // Moving the start keeps the length; the media's remaining length caps it.
      const wanted = has(args, 'end') ? span.end : span.start + (current.end - current.start);
      const longest = (next.mediaDuration - next.trimStart) / next.speed;
      const end = Math.min(wanted, span.start + longest, duration);
      next = { ...next, start: span.start, end, trimEnd: next.trimStart + (end - span.start) * next.speed };
    } else {
      next = { ...next, start: span.start, end: span.end };
    }
    applied.push('timing');
  }
  const speed = number(args, 'speed', 0.25, 4);
  if (speed !== undefined) {
    if (!isPlayableMedia(next) || (next.type !== 'video' && next.type !== 'audio')) {
      throw new CallError('not_media', 'speed applies to video and audio layers');
    }
    const out = applySpeedChange({
      start: next.start, trimStart: next.trimStart, trimEnd: next.trimEnd, speed, clipDuration: duration,
    });
    next = { ...next, ...out };
    applied.push('speed');
  }
  const placement = oneOf<MediaPlacement>(args, 'placement', MEDIA_PLACEMENTS);
  const rect = rectArg(args);
  if (placement || rect) {
    if (next.type === 'audio') throw new CallError('not_visual', 'Audio layers have no position');
    const mediaAspect = next.type === 'text' ? null : (next.rect.w / next.rect.h) * env.compAspect;
    next = {
      ...next,
      rect: next.type === 'text'
        ? (rect ?? next.rect)
        : placeMediaRect({ placement: placement ?? 'rect', rect: rect ?? next.rect, mediaAspect, compAspect: env.compAspect }),
    };
    applied.push('position');
  }
  const opacity = number(args, 'opacity', 0, 100);
  if (opacity !== undefined) {
    next = { ...next, opacity: opacity / 100 };
    applied.push('opacity');
  }
  const volume = number(args, 'volume', 0, 100);
  if (volume !== undefined) {
    if (next.type !== 'video' && next.type !== 'audio') throw new CallError('not_media', 'volume applies to video and audio layers');
    next = { ...next, volume: volume / 100 };
    applied.push('volume');
  }
  if (!applied.length) throw new CallError('nothing_to_change');
  const layers = draft.layers.map((layer) => (layer.id === id ? next : layer));
  assertSfxConcurrency(layers, next);
  return {
    draft: { ...draft, layers },
    result: { id, applied, start: next.start, end: next.end },
  };
}

/** A new natural timeline: transitions follow their scenes and framing is
 *  split at the new scene edges (both as the editor's timeline does). */
function withScenes(draft: EditorDraft, scenes: Scene[]): EditorDraft {
  return {
    ...draft,
    scenes,
    transitions: reconcileTransitions(draft.scenes, scenes, draft.transitions),
    cropKeyframes: draft.cropKeyframes.length
      ? splitSceneCropKeyframes(draft.cropKeyframes, scenes)
      : draft.cropKeyframes,
  };
}

function editTimeline(draft: EditorDraft, call: ClientCall, args: Args, env: EditorEnv): EditorDraft {
  switch (call.tool_name) {
    case 'editor_cut_ranges':
      return withScenes(draft, cutClipRanges(draft.scenes, Array.isArray(args.ranges) ? args.ranges : [], env.nextSceneId));
    case 'editor_trim':
      return withScenes(draft, trimClip(draft.scenes, {
        start: has(args, 'start') ? Number(args.start) : undefined,
        end: has(args, 'end') ? Number(args.end) : undefined,
      }));
    case 'editor_extend':
      return withScenes(draft, extendClip(draft.scenes, {
        before: has(args, 'before') ? Number(args.before) : undefined,
        after: has(args, 'after') ? Number(args.after) : undefined,
      }, env.sourceDuration));
    case 'editor_split':
      return withScenes(draft, splitSceneAt(draft.scenes, Number(args.time), env.nextSceneId));
    case 'editor_remove_scene':
      return withScenes(draft, removeScenes(
        draft.scenes,
        Array.isArray(args.indexes) ? args.indexes.map(Number) : [Number(args.index)],
      ));
    case 'editor_reorder_scenes':
      return withScenes(draft, reorderScenes(draft.scenes, Array.isArray(args.order) ? args.order.map(Number) : []));
    default:
      throw new CallError('tool_unavailable');
  }
}

const TIMELINE_TOOLS = new Set([
  'editor_cut_ranges', 'editor_trim', 'editor_extend', 'editor_split', 'editor_remove_scene', 'editor_reorder_scenes',
]);

const CENTERED = { x: 50, y: 50 };

/** The framing a scene opens on: the keyframe starting at its start, not the
 *  previous shot's, which ends at the same cut instant. */
const sceneStartFraming = (keyframes: CropCenterKeyframe[], scene: { startTime: number; endTime: number }) => (
  resolveSceneCropTarget(keyframes, [scene], scene.startTime)?.keyframe ?? null
);

function setFraming(draft: EditorDraft, args: Args, env: EditorEnv) {
  if (!env.framingAvailable) {
    throw new CallError('framing_unavailable', 'The preview shows the whole frame (no crop to move)');
  }
  const index = Number(args.scene);
  if (!Number.isInteger(index) || index < 0 || index >= draft.scenes.length) {
    throw new CallError('invalid_scene', `scene must be 0-${draft.scenes.length - 1}`);
  }
  const scene = draft.scenes[index];
  // No keyframes yet = the preview is centered: start every scene there.
  const base = draft.cropKeyframes.length
    ? draft.cropKeyframes
    : draft.scenes.map((each) => ({ time: each.startTime, endTime: each.endTime, ...CENTERED }));
  const current: { x: number; y: number; zoom?: number } = sceneStartFraming(base, scene) ?? CENTERED;
  const x = number(args, 'x', 0, 100);
  const y = number(args, 'y', 0, 100);
  const zoom = number(args, 'zoom', 1, 3);
  if (x === undefined && y === undefined && zoom === undefined) throw new CallError('nothing_to_change');
  const framing = {
    x: x ?? current.x,
    y: y ?? current.y,
    zoom: zoom ?? current.zoom,
  };
  const range = { start: scene.startTime, end: scene.endTime };
  const framed = applySceneCropFraming({ keyframes: base, scenes: draft.scenes, range, framing });
  // The scene now has one framing: merge its sections (e.g. a camera move's
  // steps) back into one, so the timeline shows a single framing control.
  const inside = framed.filter((keyframe) => keyframe.time >= range.start && keyframe.endTime <= range.end);
  const cropKeyframes = inside.length > 1
    ? [
      ...framed.filter((keyframe) => !inside.includes(keyframe)),
      {
        ...inside[0],
        time: Math.min(...inside.map((keyframe) => keyframe.time)),
        endTime: Math.max(...inside.map((keyframe) => keyframe.endTime)),
      },
    ].sort((a, b) => a.time - b.time)
    : framed;
  return {
    draft: {
      ...draft,
      cropKeyframes,
      // A plain reframe replaces a camera move on the scene.
      keyframeTransitions: draft.keyframeTransitions.filter((transition) => !isInsideRange(transition, range)),
    },
    result: { scene: index, x: framing.x, y: framing.y, zoom: framing.zoom ?? 1 },
  };
}

function framingArg(args: Args, key: string, fallback: CameraFraming): CameraFraming {
  if (!has(args, key)) return fallback;
  const value = args[key];
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new CallError('invalid_framing', key);
  const framing = value as Args;
  return {
    x: number(framing, 'x', 0, 100) ?? fallback.x,
    y: number(framing, 'y', 0, 100) ?? fallback.y,
    zoom: number(framing, 'zoom', 1, 3) ?? fallback.zoom,
  };
}

function cameraMove(draft: EditorDraft, args: Args, env: EditorEnv) {
  if (!env.framingAvailable) {
    throw new CallError('framing_unavailable', 'The preview shows the whole frame (no crop to move)');
  }
  const index = Number(args.scene);
  if (!Number.isInteger(index) || index < 0 || index >= draft.scenes.length) {
    throw new CallError('invalid_scene', `scene must be 0-${draft.scenes.length - 1}`);
  }
  const scene = draft.scenes[index];
  const base = draft.cropKeyframes.length
    ? draft.cropKeyframes
    : draft.scenes.map((each) => ({ time: each.startTime, endTime: each.endTime, ...CENTERED }));
  const current = sceneStartFraming(base, scene);
  const from = framingArg(args, 'from', { x: current?.x ?? 50, y: current?.y ?? 50, zoom: current?.zoom ?? 1 });
  const to = framingArg(args, 'to', from);
  if (from.x === to.x && from.y === to.y && from.zoom === to.zoom) {
    throw new CallError('nothing_to_change', 'Give `to` a framing different from the start');
  }
  const plan = planCameraMove({
    keyframes: base,
    transitions: draft.keyframeTransitions,
    scenes: draft.scenes,
    range: { start: scene.startTime, end: scene.endTime },
    from,
    to,
  });
  if (!plan) throw new CallError('scene_too_short', 'A camera move needs a scene of at least 0.5 s');
  return {
    draft: { ...draft, cropKeyframes: plan.keyframes, keyframeTransitions: plan.transitions },
    result: { scene: index, from, to, seconds: Math.round((scene.endTime - scene.startTime) * 100) / 100 },
  };
}

export function applyEditorCalls(
  initial: EditorDraft,
  calls: readonly ClientCall[],
  env: EditorEnv,
): EditorBatchOutcome {
  let draft = initial;
  const results: ClientResult[] = [];
  const changed = new Set<EditorArea>();
  let templateId: string | null = null;
  let seek: EditorBatchOutcome['seek'] = null;
  let selectClipIndex: number | null = null;
  let cover: EditorBatchOutcome['cover'] = null;
  let layout: LayoutCall | null = null;
  const alone = calls.length === 1;

  calls.forEach((call) => {
    const args: Args = call.args && typeof call.args === 'object' ? call.args : {};
    try {
      if (['editor_motion_reference', 'editor_add_motion', 'editor_get_motion', 'editor_update_motion', 'editor_add_captions'].includes(call.tool_name)) {
        const out = applyMotionTool(draft, call.tool_name, args, env);
        draft = out.draft;
        if (out.changed) changed.add('layers');
        if (out.subtitleChanged) changed.add('subtitle');
        results.push({ id: call.id, ok: true, result: out.result });
        return;
      }
      if (TIMELINE_TOOLS.has(call.tool_name)) {
        const durationBefore = clipDurationOf(draft.scenes);
        draft = editTimeline(draft, call, args, env);
        changed.add('timeline');
        const duration = clipDurationOf(draft.scenes);
        const tenth = (value: number) => Math.round(value * 10) / 10;
        results.push({
          id: call.id,
          ok: true,
          // Removed (or added, negative) seconds, so the reply can say what changed.
          result: { scenes: draft.scenes.length, duration: tenth(duration), removed_seconds: tenth(durationBefore - duration) },
        });
        return;
      }
      switch (call.tool_name) {
        case 'editor_set_subtitle_style': {
          const out = setSubtitleStyle(draft, args, env);
          draft = out.draft;
          changed.add('subtitle');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_apply_template': {
          const wanted = typeof args.template === 'string' ? args.template : '';
          const template = env.templates.find((candidate) => candidate.id === wanted)
            ?? env.templates.find((candidate) => normalizeName(candidate.name) === normalizeName(wanted));
          if (!template) throw new CallError('unknown_template', 'Use an id or name from options.templates');
          if (!template.canApply) throw new CallError('template_requires_pro');
          templateId = template.id;
          draft = { ...draft, subtitle: { ...template.subtitle }, fontId: template.fontId || draft.fontId };
          changed.add('subtitle');
          results.push({ id: call.id, ok: true, result: { applied: template.name } });
          break;
        }
        case 'editor_add_text': {
          const out = addText(draft, args, env);
          draft = out.draft;
          changed.add('layers');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_update_text': {
          const out = updateText(draft, args, env);
          draft = out.draft;
          changed.add('layers');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_remove_layer': {
          const out = removeLayer(draft, args);
          draft = out.draft;
          changed.add('layers');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_add_effect': {
          const out = addEffect(draft, args, env);
          draft = out.draft;
          changed.add('effects');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_remove_effect': {
          const out = removeEffect(draft, args);
          draft = out.draft;
          changed.add('effects');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_set_transition': {
          const out = setTransition(draft, args);
          draft = out.draft;
          changed.add('transitions');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_set_look': {
          const out = setLook(draft, args);
          draft = out.draft;
          changed.add('effects');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_set_framing': {
          const out = setFraming(draft, args, env);
          draft = out.draft;
          changed.add('framing');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_camera_move': {
          const out = cameraMove(draft, args, env);
          draft = out.draft;
          changed.add('framing');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_add_media': {
          const out = addMedia(draft, args, env);
          draft = out.draft;
          changed.add('layers');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_update_layer': {
          const out = updateLayer(draft, args, env);
          draft = out.draft;
          changed.add('layers');
          results.push({ id: call.id, ok: true, result: out.result });
          break;
        }
        case 'editor_set_layout': {
          layout = validateLayoutCall(args, {
            catalog: env.layoutCatalog, enforced: env.layoutEnforced, fonts: env.fonts, media: env.media,
          });
          results.push({
            id: call.id,
            ok: true,
            result: { kind: layout.kind, preset: layout.preset ?? null, fields: layout.fields.map((field) => field.key) },
          });
          break;
        }
        case 'editor_set_cover': {
          const at = number(args, 'time', 0, clipDurationOf(draft.scenes));
          if (at === undefined) throw new CallError('invalid_time');
          cover = { time: at, callId: call.id };
          results.push({ id: call.id, ok: true, result: { time: at } });
          break;
        }
        case 'editor_seek': {
          const at = number(args, 'time', 0, clipDurationOf(draft.scenes));
          if (at === undefined) throw new CallError('invalid_time');
          seek = { time: at, play: bool(args, 'play') === true };
          results.push({ id: call.id, ok: true, result: { time: at } });
          break;
        }
        case 'editor_select_clip': {
          if (!alone) throw new CallError('select_clip_alone', 'Call editor_select_clip in its own step');
          const index = Number(args.index);
          if (!Number.isInteger(index) || index < 1 || index > env.clipCount) {
            throw new CallError('invalid_clip', `index must be 1-${env.clipCount}`);
          }
          selectClipIndex = index;
          results.push({ id: call.id, ok: true, result: { selected: index } });
          break;
        }
        default:
          throw new CallError('tool_unavailable');
      }
    } catch (error) {
      let failure = new CallError('invalid_arguments');
      if (error instanceof CallError) failure = error;
      else if (error instanceof SceneEditError || error instanceof LayoutCallError || error instanceof MotionToolError) {
        failure = new CallError(error.code, error.detail);
      }
      results.push({
        id: call.id,
        ok: false,
        error: failure.code,
        ...(failure.detail ? { message: failure.detail } : {}),
      });
    }
  });

  return {
    draft, results, changed, templateId, seek, selectClipIndex, cover, layout,
  };
}
